/**
 * --------------------------------------------------------------------------
 * API Fetch Wrapper for Shopify
 * --------------------------------------------------------------------------
 *
 * This function wraps the native browser `fetch` API to create a centralized
 * place for handling API requests in your Shopify theme. It relies on global
 * variables (`urlBase`, `accessToken`) and functions (`globalLogout`)
 * defined in a separate script file (e.g., _global-scripts.liquid).
 *
 * Key Features:
 * 1.  **Automatic 403 (Forbidden) Handling**: If any API call returns a 403 status,
 * it's assumed the user's session/token is invalid, and it calls the global
 * logout function.
 * 2.  **Centralized Headers**: Automatically attaches required headers like
 * 'Content-Type' and the 'Authorization' bearer token to every request.
 * 3.  **Simplified API Calls**: Reduces code repetition in your theme, as you
 * no longer need to write headers and error checks for every single fetch.
 * 4.  **Graceful Error Handling**: Throws a clear error for other non-successful
 * HTTP statuses (like 404 or 500), which can be caught by the calling code.
 */

// This script assumes that 'urlBase' and 'accessToken' are defined globally.
// It also assumes a 'globalLogout()' function is available for logging out.

/**
 * A wrapper for the fetch API that includes default headers and centralized error handling.
 * @param {string} endpoint - The API endpoint to call (e.g., '/user/account').
 * @param {object} [options={}] - Optional fetch options (method, body, custom headers).
 * @returns {Promise<any>} A promise that resolves with the JSON response data.
 * @throws {Error} Throws an error for non-2xx responses that aren't 403.
 */
async function apiFetch(endpoint, options = {}) {
  // 1. Define default headers for every request
  const defaultHeaders = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  };
console.log('xxxxxxx',endpoint)
  // Add the Authorization header only if the global accessToken exists
  if (typeof accessToken !== 'undefined' && accessToken) {
    defaultHeaders['Authorization'] = `Bearer ${accessToken}`;
  }

  // 2. Merge default options with any custom options provided
  const finalOptions = {
    ...options, // Custom options (like method, body) are spread first
    headers: {
      ...defaultHeaders, // Then default headers
      ...options.headers, // Then any custom headers, which can override defaults
    },
  };

  try {
    // Use the global 'urlBase' variable
    const response = await fetch(`${urlBase}${endpoint}`, finalOptions);

    // 3. CENTRALIZED ERROR HANDLING
    // Check for 403 Forbidden status specifically
    if (response.status === 403) {
      // This is our "catch-all". The token is bad, so call the global logout function.
      globalLogout();
      // Throw an error to stop any further code execution in the calling function
      throw new Error('403 Forbidden: User is not authorized. Logging out.');
    }

    // Check for any other non-successful status codes (e.g., 404, 500)
    if (!response.ok) {
      // For other errors, we just throw a generic error to be handled by the caller
      throw new Error(`API Error: ${response.status} ${response.statusText}`);
    }

    // 4. If the response is successful, parse and return the JSON
    // Handle cases where the response might be empty
    const contentType = response.headers.get("content-type");
    if (contentType && contentType.indexOf("application/json") !== -1) {
        return response.json();
    } else {
        return response.text();
    }


  } catch (error) {
    console.error(`Fetch failed for endpoint: ${endpoint}`, error);
    // Re-throw the error so the calling function's .catch() block can handle it
    throw error;
  }
}

// --- Example Usage ---

// To use this, you would first include this script in your theme.liquid,
// and then in your other scripts, you can call apiFetch instead of fetch.

/*
async function getAccountData() {
  try {
    document.getElementById("account-loading").style.display = "block";
    const data = await apiFetch('/user/account'); // No need for method/headers
    console.log('Account data:', data);
    // ... process the data
  } catch (error) {
    // This will only catch non-403 errors, because a 403 will cause a page redirect.
    console.error('Failed to get account data:', error);
    // ... display an error message to the user
  } finally {
    document.getElementById("account-loading").style.display = "none";
  }
}

async function updateProfile(profileData) {
  try {
    const options = {
      method: 'POST',
      body: JSON.stringify(profileData),
    };
    const result = await apiFetch('/user/profile', options);
    console.log('Profile updated:', result);
  } catch (error) {
    console.error('Failed to update profile:', error);
  }
}
*/

